import { Component } from 'react'
import styled from 'styled-components'
import { X, Calendar, Clock, FileText, MapPin, Users, Lock, Save, User } from 'lucide-react'
import CalendarService from '../../Services/CalendarService'
import swaller from '../Tools/Swaller'
import i18n from '../../i18n'

export default class EventFormModal extends Component {
  constructor (props) {
    super(props)

    const editingEvent = props.event
    const now = new Date()
    const defaultStart = props.selectedDate || now
    const defaultEnd = new Date(defaultStart.getTime() + 60 * 60 * 1000) // +1 ora

    this.state = {
      title: editingEvent?.title || '',
      description: editingEvent?.description || '',
      location: editingEvent?.location || '',
      startDate: editingEvent?.startDate
        ? this.formatDateForInput(new Date(editingEvent.startDate))
        : this.formatDateForInput(defaultStart),
      startTime: editingEvent?.startDate && !editingEvent.isAllDay
        ? this.formatTimeForInput(new Date(editingEvent.startDate))
        : this.formatTimeForInput(defaultStart),
      endDate: editingEvent?.endDate
        ? this.formatDateForInput(new Date(editingEvent.endDate))
        : this.formatDateForInput(defaultEnd),
      endTime: editingEvent?.endDate && !editingEvent.isAllDay
        ? this.formatTimeForInput(new Date(editingEvent.endDate))
        : this.formatTimeForInput(defaultEnd),
      isAllDay: editingEvent?.isAllDay || false,
      isShared: editingEvent?._isShared || false,
      selectedUsers: editingEvent?._sharedWith?.map(u => u._id || u) || [],
      selectedCliente: editingEvent?.cliente?._id || editingEvent?.cliente || '',
      companyUsers: [],
      clienti: [],
      loading: false,
      loadingUsers: false,
      loadingClienti: false
    }
  }

  async componentDidMount () {
    // Carica lista utenti azienda se necessario
    if (this.state.isShared || this.props.event?._isShared) {
      await this.loadCompanyUsers()
    }
    // Carica lista clienti
    await this.loadClienti()
  }

  formatDateForInput = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  formatTimeForInput = (date) => {
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${hours}:${minutes}`
  }

  loadCompanyUsers = async () => {
    this.setState({ loadingUsers: true })
    try {
      const users = await CalendarService.getCompanyUsers()
      this.setState({ companyUsers: users || [] })
    } catch (error) {
      console.error('Error loading company users:', error)
      swaller.error(i18n.t('calendar.errorLoadingUsers', { ns: 'common' }))
    } finally {
      this.setState({ loadingUsers: false })
    }
  }

  loadClienti = async () => {
    this.setState({ loadingClienti: true })
    try {
      const clienti = await CalendarService.getClients()
      this.setState({ clienti: clienti || [] })
    } catch (error) {
      console.error('Error loading clients:', error)
      // Non mostra errore all'utente, il campo cliente è opzionale
    } finally {
      this.setState({ loadingClienti: false })
    }
  }

  handleInputChange = (field, value) => {
    this.setState({ [field]: value })
  }

  handleSharedToggle = async (value) => {
    this.setState({ isShared: value })
    if (value && this.state.companyUsers.length === 0) {
      await this.loadCompanyUsers()
    }
    if (!value) {
      this.setState({ selectedUsers: [] })
    }
  }

  handleUserToggle = (userId) => {
    this.setState(prev => {
      const isSelected = prev.selectedUsers.includes(userId)
      return {
        selectedUsers: isSelected
          ? prev.selectedUsers.filter(id => id !== userId)
          : [...prev.selectedUsers, userId]
      }
    })
  }

  validateForm = () => {
    const { title, startDate, startTime, endDate, endTime, isAllDay } = this.state

    if (!title.trim()) {
      swaller.error(i18n.t('calendar.errorTitleRequired', { ns: 'common' }))
      return false
    }

    if (!startDate) {
      swaller.error(i18n.t('calendar.errorStartDateRequired', { ns: 'common' }))
      return false
    }

    const oggi = this.formatDateForInput(new Date())
    const originale = this.props.event?.startDate && this.formatDateForInput(new Date(this.props.event.startDate))
    if (startDate < oggi && startDate !== originale) {
      swaller.error(i18n.t('calendar.errorPastDate', { ns: 'common', defaultValue: 'Non puoi creare eventi in giorni passati' }))
      return false
    }

    if (!isAllDay && !startTime) {
      swaller.error(i18n.t('calendar.errorStartTimeRequired', { ns: 'common' }))
      return false
    }

    if (!isAllDay && endDate && endTime) {
      const start = new Date(`${startDate}T${startTime}`)
      const end = new Date(`${endDate}T${endTime}`)

      if (end <= start) {
        swaller.error(i18n.t('calendar.errorEndBeforeStart', { ns: 'common' }))
        return false
      }
    }

    return true
  }

  handleSubmit = async () => {
    if (!this.validateForm()) return

    this.setState({ loading: true })

    try {
      const { title, description, location, startDate, startTime, endDate, endTime, isAllDay, isShared, selectedUsers, selectedCliente } = this.state

      // Formato per il backend
      const eventData = {
        title,
        description,
        location,
        startDate: isAllDay
          ? startDate
          : new Date(`${startDate}T${startTime}`).toISOString(),
        endDate: isAllDay
          ? (endDate || startDate)
          : new Date(`${endDate || startDate}T${endTime || startTime}`).toISOString(),
        isAllDay,
        isPrivate: !isShared,
        sharedWith: isShared ? selectedUsers : [],
        cliente: selectedCliente || null
      }

      if (this.props.event?._id) {
        // Modifica evento esistente
        await CalendarService.updateEvent(this.props.event._id, eventData)
      } else {
        // Crea nuovo evento
        await CalendarService.createEvent(eventData)
      }

      // Callback e chiusura
      if (this.props.onSuccess) {
        this.props.onSuccess()
      }

      this.props.onClose()
    } catch (error) {
      console.error('Error saving event:', error)
      swaller.error(i18n.t('calendar.errorSavingEvent', { ns: 'common' }))
    } finally {
      this.setState({ loading: false })
    }
  }

  handleClose = (e) => {
    if (e.target === e.currentTarget) {
      this.props.onClose()
    }
  }

  render () {
    const {
      title, description, location,
      startDate, startTime, endDate, endTime,
      isAllDay, isShared, selectedUsers, companyUsers,
      selectedCliente, clienti,
      loading, loadingUsers, loadingClienti
    } = this.state

    const isEditing = !!this.props.event?._id

    return (
      <ModalOverlay onClick={this.handleClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <Title>{isEditing ? i18n.t('calendar.editEventTitle', { ns: 'common' }) : i18n.t('calendar.newEvent', { ns: 'common' })}</Title>
            <CloseButton onClick={this.props.onClose} disabled={loading}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <FormSection>
              <FormGroup>
                <Label required>
                  <FileText size={18} />
                  <span>{i18n.t('calendar.titleLabel', { ns: 'common' })}</span>
                </Label>
                <Input
                  type='text'
                  placeholder={i18n.t('calendar.titlePlaceholder', { ns: 'common' })}
                  value={title}
                  onChange={(e) => this.handleInputChange('title', e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <FileText size={18} />
                  <span>{i18n.t('calendar.descriptionLabel', { ns: 'common' })}</span>
                </Label>
                <TextArea
                  placeholder={i18n.t('calendar.descriptionPlaceholder', { ns: 'common' })}
                  value={description}
                  onChange={(e) => this.handleInputChange('description', e.target.value)}
                  disabled={loading}
                  rows={3}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <MapPin size={18} />
                  <span>{i18n.t('calendar.locationLabel', { ns: 'common' })}</span>
                </Label>
                <Input
                  type='text'
                  placeholder={i18n.t('calendar.locationPlaceholder', { ns: 'common' })}
                  value={location}
                  onChange={(e) => this.handleInputChange('location', e.target.value)}
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <User size={18} />
                  <span>{i18n.t('calendar.clientLabel', { ns: 'common' })}</span>
                </Label>
                {loadingClienti
                  ? (
                    <Input disabled value={i18n.t('calendar.loadingClients', { ns: 'common' })} />
                    )
                  : (
                    <Select
                      value={selectedCliente}
                      onChange={(e) => this.handleInputChange('selectedCliente', e.target.value)}
                      disabled={loading}
                    >
                      <option value=''>{i18n.t('calendar.noClient', { ns: 'common' })}</option>
                      {clienti.map(cliente => (
                        <option key={cliente._id} value={cliente._id}>
                          {cliente.nome && cliente.cognome
                            ? `${cliente.nome} ${cliente.cognome}`
                            : cliente.intestazione || i18n.t('calendar.clientNoName', { ns: 'common' })}
                        </option>
                      ))}
                    </Select>
                    )}
              </FormGroup>

              <CheckboxGroup>
                <Checkbox
                  type='checkbox'
                  id='allday'
                  checked={isAllDay}
                  onChange={(e) => this.handleInputChange('isAllDay', e.target.checked)}
                  disabled={loading}
                />
                <CheckboxLabel htmlFor='allday'>
                  {i18n.t('calendar.allDayEvent', { ns: 'common' })}
                </CheckboxLabel>
              </CheckboxGroup>

              <DateTimeRow>
                <FormGroup $flex>
                  <Label required>
                    <Calendar size={18} />
                    <span>{i18n.t('calendar.startDate', { ns: 'common' })}</span>
                  </Label>
                  <Input
                    type='date'
                    min={this.formatDateForInput(new Date())}
                    value={startDate}
                    onChange={(e) => this.handleInputChange('startDate', e.target.value)}
                    disabled={loading}
                  />
                </FormGroup>

                {!isAllDay && (
                  <FormGroup $flex>
                    <Label required>
                      <Clock size={18} />
                      <span>{i18n.t('calendar.startTime', { ns: 'common' })}</span>
                    </Label>
                    <Input
                      type='time'
                      value={startTime}
                      onChange={(e) => this.handleInputChange('startTime', e.target.value)}
                      disabled={loading}
                    />
                  </FormGroup>
                )}
              </DateTimeRow>

              <DateTimeRow>
                <FormGroup $flex>
                  <Label>
                    <Calendar size={18} />
                    <span>{i18n.t('calendar.endDate', { ns: 'common' })}</span>
                  </Label>
                  <Input
                    type='date'
                    value={endDate}
                    onChange={(e) => this.handleInputChange('endDate', e.target.value)}
                    disabled={loading}
                  />
                </FormGroup>

                {!isAllDay && (
                  <FormGroup $flex>
                    <Label>
                      <Clock size={18} />
                      <span>{i18n.t('calendar.endTime', { ns: 'common' })}</span>
                    </Label>
                    <Input
                      type='time'
                      value={endTime}
                      onChange={(e) => this.handleInputChange('endTime', e.target.value)}
                      disabled={loading}
                    />
                  </FormGroup>
                )}
              </DateTimeRow>
            </FormSection>

            <Divider />

            <FormSection>
              <SectionTitle>{i18n.t('calendar.visibility', { ns: 'common' })}</SectionTitle>

              <ToggleGroup>
                <ToggleOption
                  $active={!isShared}
                  onClick={() => this.handleSharedToggle(false)}
                  disabled={loading}
                >
                  <Lock size={18} />
                  <div>
                    <ToggleTitle>{i18n.t('calendar.privateTitle', { ns: 'common' })}</ToggleTitle>
                    <ToggleDescription>{i18n.t('calendar.privateDesc', { ns: 'common' })}</ToggleDescription>
                  </div>
                </ToggleOption>

                <ToggleOption
                  $active={isShared}
                  onClick={() => this.handleSharedToggle(true)}
                  disabled={loading}
                >
                  <Users size={18} />
                  <div>
                    <ToggleTitle>{i18n.t('calendar.sharedTitle', { ns: 'common' })}</ToggleTitle>
                    <ToggleDescription>{i18n.t('calendar.sharedDesc', { ns: 'common' })}</ToggleDescription>
                  </div>
                </ToggleOption>
              </ToggleGroup>

              {isShared && (
                <UserSelectionContainer>
                  {loadingUsers
                    ? (
                      <LoadingUsers>{i18n.t('calendar.loadingUsers', { ns: 'common' })}</LoadingUsers>
                      )
                    : companyUsers.length === 0
                      ? (
                        <NoUsers>{i18n.t('calendar.noUsers', { ns: 'common' })}</NoUsers>
                        )
                      : (
                        <>
                          <UserSelectionHeader>
                            {i18n.t('calendar.selectColleagues', { ns: 'common' })}
                          </UserSelectionHeader>
                          <UserList>
                            {companyUsers.map(user => (
                              <UserItem key={user._id}>
                                <Checkbox
                                  type='checkbox'
                                  id={`user-${user._id}`}
                                  checked={selectedUsers.includes(user._id)}
                                  onChange={() => this.handleUserToggle(user._id)}
                                  disabled={loading}
                                />
                                <UserLabel htmlFor={`user-${user._id}`}>
                                  <UserName>{user.nome} {user.cognome}</UserName>
                                  <UserEmail>{user.email}</UserEmail>
                                </UserLabel>
                              </UserItem>
                            ))}
                          </UserList>
                          {selectedUsers.length > 0 && (
                            <SelectedCount>
                              {selectedUsers.length} {selectedUsers.length === 1
                                ? i18n.t('calendar.userSelected', { ns: 'common' })
                                : i18n.t('calendar.usersSelected', { ns: 'common' })}
                            </SelectedCount>
                          )}
                        </>
                        )}
                </UserSelectionContainer>
              )}
            </FormSection>
          </ModalBody>

          <ModalFooter>
            <CancelButton onClick={this.props.onClose} disabled={loading}>
              {i18n.t('cancel', { ns: 'common' })}
            </CancelButton>
            <SaveButton onClick={this.handleSubmit} disabled={loading}>
              <Save size={18} />
              <span>{loading
                ? i18n.t('loading', { ns: 'common' })
                : (isEditing ? i18n.t('calendar.saveChanges', { ns: 'common' }) : i18n.t('calendar.createEvent', { ns: 'common' }))}</span>
            </SaveButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    )
  }
}

// ============ STYLED COMPONENTS ============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.3)'};
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

const ModalContainer = styled.div`
  background: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.radius.card};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    width: 95%;
    max-height: 95vh;
  }
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.medium};
`

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text.tertiary};
  cursor: pointer;
  border-radius: ${props => props.theme.radius.button};
  transition: all 0.2s ease;

  svg {
    font-size: 1.25rem;
  }

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.background.hover};
    color: ${props => props.theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 1.5rem;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background.secondary};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border.medium};
    border-radius: 4px;
  }
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ${props => props.$flex && 'flex: 1;'}
`

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.secondary};

  svg {
    font-size: 0.875rem;
  }

  ${props => props.required && `
    &::after {
      content: '*';
      color: ${props.theme.colors.semantic.danger};
      margin-left: -0.25rem;
    }
  `}
`

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  background: ${props => props.theme.colors.background.primary};
  color: ${props => props.theme.colors.text.primary};
  font-size: 0.875rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.semantic.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.semantic.primary}15;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
  }
`

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  background: ${props => props.theme.colors.background.primary};
  color: ${props => props.theme.colors.text.primary};
  font-size: 0.875rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.semantic.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.semantic.primary}15;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${props => props.theme.colors.background.primary};
    color: ${props => props.theme.colors.text.primary};
  }
`

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  background: ${props => props.theme.colors.background.primary};
  color: ${props => props.theme.colors.text.primary};
  font-size: 0.875rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.semantic.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.semantic.primary}15;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
  }
`

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
`

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${props => props.theme.colors.semantic.primary};

  &:disabled {
    cursor: not-allowed;
  }
`

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  user-select: none;
`

const DateTimeRow = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const Divider = styled.div`
  height: 1px;
  background: ${props => props.theme.colors.border.medium};
  margin: 1rem 0;
`

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.75rem 0;
`

const ToggleGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ToggleOption = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: ${props => props.$active ? props.theme.colors.semantic.primary + '15' : props.theme.colors.background.hover};
  border: 2px solid ${props => props.$active ? props.theme.colors.semantic.primary : props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  svg {
    font-size: 1.25rem;
    color: ${props => props.$active ? props.theme.colors.semantic.primary : props.theme.colors.text.secondary};
    margin-top: 0.125rem;
  }

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.semantic.primary};
    background: ${props => props.theme.colors.semantic.primary}10;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ToggleTitle = styled.div`
  font-size: 0.938rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.25rem;
`

const ToggleDescription = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.text.tertiary};
  line-height: 1.4;
`

const UserSelectionContainer = styled.div`
  background: ${props => props.theme.colors.background.hover};
  border-radius: ${props => props.theme.radius.input};
  padding: 1rem;
  margin-top: 0.75rem;
`

const UserSelectionHeader = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 0.75rem;
`

const LoadingUsers = styled.div`
  text-align: center;
  padding: 1.5rem;
  color: ${props => props.theme.colors.text.muted};
  font-size: 0.875rem;
`

const NoUsers = styled.div`
  text-align: center;
  padding: 1.5rem;
  color: ${props => props.theme.colors.text.muted};
  font-size: 0.875rem;
`

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border.medium};
    border-radius: 3px;
  }
`

const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem;
  background: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.radius.button};
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.background.secondary};
  }
`

const UserLabel = styled.label`
  flex: 1;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  user-select: none;
`

const UserName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary};
`

const UserEmail = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.text.tertiary};
`

const SelectedCount = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${props => props.theme.colors.border.medium};
  font-size: 0.813rem;
  color: ${props => props.theme.colors.text.secondary};
  text-align: center;
`

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem 1.75rem;
  border-top: 1px solid ${props => props.theme.colors.border.medium};
  gap: 0.75rem;
`

const CancelButton = styled.button`
  padding: 0.625rem 1.25rem;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.button};
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.background.hover};
    border-color: ${props => props.theme.colors.border.strong};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: ${props => props.theme.colors.semantic.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: none;
  border-radius: ${props => props.theme.radius.button};
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: ${props => props.theme.transitions.button};

  svg {
    font-size: 0.875rem;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
